import React, { useEffect, useState } from 'react';
import { Search, Loader2, Check } from 'lucide-react';
import sessionsService from '../../services/sessionsServices';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useParams } from 'react-router-dom';
import projectService from '../../services/projectService';
import Toast from '../Toast';
import { PLATFORM_ID } from '../../constants/platform';

/**
 * Add existing chats to a project (dark overlay style).
 * API unchanged:
 *  - sessionsService.GetSessionsList
 *  - projectService.createProjectSessions
 */
const AddChatModal = ({ onClose, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const { id } = useParams();

  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const payload = {
    platform_id: PLATFORM_ID,
    user_id: user?.data?.ID,
    project_id: 'test',
  };
  const { sessions_loading, sessions_data } = sessionsService.GetSessionsList(payload);
  const [sessionData, setSessionData] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);

  const handleToggle = session => {
    setSelectedSessions(prev => {
      const exists = prev.some(item => item.session_id === session.id);
      if (exists) {
        return prev.filter(item => item.session_id !== session.id);
      }
      return [
        ...prev,
        {
          session_id: session?.id,
          initial_message: session?.initial_message,
          updated_at: session?.updated_at,
          title: session?.title,
        },
      ];
    });
  };

  const AddSession = async () => {
    setLoading(true);
    try {
      const resp = await projectService.createProjectSessions({
        project_id: id,
        sessions: selectedSessions,
      });
      refetch();
      setLoading(false);
      onClose();
      Toast({ type: 'success', message: resp?.detail });
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessions_data) {
      function mergeSessions(sessions) {
        return Object.values(sessions)
          .flat()
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      }
      setSessionData(mergeSessions(sessions_data?.sessions));
    }
  }, [sessions_data]);

  const stripHtml = html => (html || '').replace(/<[^>]*>/g, '');
  const filteredSessions = sessionData.filter(elem => {
    if (!query) return true;
    const q = query.toLowerCase();
    return stripHtml(elem?.title).toLowerCase().includes(q) || stripHtml(elem?.initial_message).toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white border border-shell-border rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-4">
          <h3 className="text-lg font-semibold text-ink mb-1">Add Chats</h3>
          <p className="text-sm text-mute mb-4">Select existing chats to add them to this project.</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search your chats"
              className="w-full bg-paper border border-shell-border rounded-lg pl-9 pr-3 py-2 text-sm text-ink placeholder:text-mute outline-none focus:border-primary/40"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-2">
          {sessions_loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <p className="text-center text-sm text-mute py-16">No chats found.</p>
          ) : (
            filteredSessions.map((elem, ind) => {
              const selected = selectedSessions.some(item => item.session_id === elem.id);
              return (
                <button
                  key={elem?.id || ind}
                  type="button"
                  onClick={() => handleToggle(elem)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                    selected
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-shell-border bg-paper hover:border-ink/20'
                  }`}>
                  <span
                    className={`mt-0.5 w-[18px] h-[18px] rounded flex-shrink-0 border flex items-center justify-center ${
                      selected ? 'bg-primary border-primary' : 'border-[#3a3a3a]'
                    }`}>
                    {selected && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong
                      className="block text-sm font-semibold text-ink truncate capitalize"
                      dangerouslySetInnerHTML={{ __html: elem.title }}
                    />
                    <span
                      className="block text-xs text-mute line-clamp-2 mt-0.5"
                      dangerouslySetInnerHTML={{ __html: elem.initial_message }}
                    />
                    <span className="block text-[10px] text-mute mt-1">{elem.updated_at}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-shell-border">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-mute hover:text-ink">
            Cancel
          </button>
          <button
            type="button"
            onClick={AddSession}
            disabled={selectedSessions.length === 0 || loading}
            className="px-4 py-2 text-sm font-medium bg-spacemint hover:bg-spacemint/85 text-spaceblack rounded-lg disabled:opacity-50">
            {loading ? 'Adding...' : `Add Chats${selectedSessions.length > 0 ? ` (${selectedSessions.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddChatModal;
